import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';

const TcqApplications = () => {
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/academy/apply`);
      if (res.ok) {
        const data = await res.json();
        setApplications(data);
      }
    } catch (err) {
      console.error('Error fetching TCQ applications:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/academy/apply/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setApplications(prev => prev.filter(a => a._id !== id));
        if (selected?._id === id) setSelected(null);
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1">
        <div className="px-5 py-4 bg-black text-white">
          <p className="capitalize text-lg" style={{ fontFamily: 'Muli' }}>TCQ Membership Applications</p>
        </div>

        <div className="p-8">
          <div className="grid xl:grid-cols-3 gap-6">
            {/* Table */}
            <div className="xl:col-span-2 overflow-x-auto">
              <table className="min-w-full border border-gray-200 bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Focus</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm" style={{ fontFamily: 'Muli' }}>No applications yet.</td>
                    </tr>
                  )}
                  {applications.map((a, i) => (
                    <tr
                      key={a._id}
                      className={`hover:bg-gray-50 cursor-pointer ${selected?._id === a._id ? 'bg-gray-50' : ''}`}
                      onClick={() => setSelected(a)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-400" style={{ fontFamily: 'Muli' }}>{i + 1}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" style={{ fontFamily: 'Muli' }}>{a.fullName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" style={{ fontFamily: 'Muli' }}>{a.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" style={{ fontFamily: 'Muli' }}>{a.focus || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-400" style={{ fontFamily: 'Muli' }}>
                        {a.submittedAt ? new Date(a.submittedAt).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(a._id); }}
                          className="text-xs text-red-500 hover:text-red-700 uppercase tracking-wider"
                          style={{ fontFamily: 'Muli' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detail panel */}
            <div className="bg-white border border-gray-200 p-6">
              {!selected ? (
                <p className="text-gray-400 text-sm uppercase tracking-widest" style={{ fontFamily: 'Muli' }}>
                  Select a row to view details
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-xs uppercase tracking-widest text-gray-400" style={{ fontFamily: 'Muli' }}>Application Detail</p>
                    <button onClick={() => setSelected(null)} className="text-gray-300 hover:text-gray-600 text-xl">×</button>
                  </div>

                  {[
                    { label: 'Full Name',   value: selected.fullName },
                    { label: 'Email',       value: selected.email },
                    { label: 'Focus',       value: selected.focus },
                    { label: 'Portfolio / Instagram', value: selected.portfolio },
                    { label: 'Submitted',   value: selected.submittedAt ? new Date(selected.submittedAt).toLocaleString('en-GB') : null },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label}>
                      <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: 'Muli' }}>{f.label}</p>
                      {f.label === 'Portfolio / Instagram' ? (
                        <a
                          href={selected.portfolio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline break-all"
                          style={{ fontFamily: 'Muli' }}
                        >
                          {selected.portfolio}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-800" style={{ fontFamily: 'Muli' }}>{f.value}</p>
                      )}
                    </div>
                  ))}

                  <button
                    onClick={() => handleDelete(selected._id)}
                    className="mt-4 w-full border border-red-300 text-red-500 py-2 text-xs uppercase tracking-widest hover:bg-red-50 transition"
                    style={{ fontFamily: 'Muli' }}
                  >
                    Delete Application
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TcqApplications;
